/**
 * The Workspaces admin page's words and sums (components/AppAdmin.tsx), kept
 * apart from React so they can be tested on their own.
 *
 * The app Worker answers GET /api/admin/workspaces with every Ark Bot
 * workspace and its user, sorted by email. The page shows each one's state,
 * and lets an operator restart a running one, or every running one at once,
 * so it starts again with the app's current settings.
 */

export interface AdminWorkspace {
  readonly id: string;
  readonly account: { readonly id: string; readonly email: string | null };
  /** "ready", "starting", "stopped" or "error"; anything else the platform adds is shown as it comes. */
  readonly state: string;
  readonly tier: string;
  /** The machine's name or ID; null while the workspace is not placed on one. */
  readonly machine: string | null;
  /** A lifecycle job under way ("backup", "restart", "reassign", "shutdown"); null when none is. */
  readonly busy: string | null;
  /** The workspace's last error message. */
  readonly error: string | null;
  /** When it was last seen ready, in ms; null if it never was. */
  readonly lastReadyAt: number | null;
}

export interface AdminWorkspacesAnswer {
  readonly workspaces: readonly AdminWorkspace[];
}

export interface RestartAllAnswer {
  readonly restarted: number;
  readonly skipped: number;
}

/** How a state pill is coloured. */
export type StateTone = "ok" | "warn" | "danger" | "idle";

const STATES: Readonly<Record<string, { readonly label: string; readonly tone: StateTone }>> = {
  ready: { label: "Running", tone: "ok" },
  starting: { label: "Starting", tone: "warn" },
  stopped: { label: "Stopped", tone: "idle" },
  error: { label: "Error", tone: "danger" },
};

/** A job under way, as the rest of a sentence: "Already busy: backing up." */
const JOBS: Readonly<Record<string, string>> = {
  backup: "backing up",
  restart: "restarting",
  reassign: "moving to another machine",
  shutdown: "shutting down",
  // The subscription ended: backed up and stopped.
  expiry: "shutting down",
};

const capital = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);
const plural = (count: number, one: string, many = `${one}s`): string => `${count} ${count === 1 ? one : many}`;

/** Who the workspace is for: the account's email, or its ID when no email is recorded. */
export function userText(workspace: Pick<AdminWorkspace, "account">): string {
  return workspace.account.email ?? workspace.account.id;
}

export function jobText(busy: string): string {
  return JOBS[busy] ?? busy;
}

/** The state pill: the job under way when there is one ("Backing up…", "Moving…"), otherwise the state. */
export function stateOf(workspace: Pick<AdminWorkspace, "state" | "busy">): { readonly label: string; readonly tone: StateTone; readonly busy: boolean } {
  if (workspace.busy != null) {
    const label = workspace.busy === "reassign" ? "Moving" : capital(jobText(workspace.busy));
    return { label: `${label}…`, tone: "warn", busy: true };
  }
  const known = STATES[workspace.state];
  return { label: known?.label ?? capital(workspace.state), tone: known?.tone ?? "idle", busy: false };
}

/** Why Restart is held for this workspace, or null when it may go: only a running workspace with nothing under way restarts. */
export function restartBlocker(workspace: Pick<AdminWorkspace, "state" | "busy"> & Partial<Pick<AdminWorkspace, "machine">>): string | null {
  if (workspace.busy != null) return `Already busy: ${jobText(workspace.busy)}.`;
  // On no machine it runs nowhere, whatever its record says; the platform refuses it too.
  if (workspace.state !== "ready" || workspace.machine === null) return "Only a running workspace can be restarted; a stopped one starts with the app’s current settings anyway.";
  return null;
}

export function canRestart(workspace: Pick<AdminWorkspace, "state" | "busy"> & Partial<Pick<AdminWorkspace, "machine">>): boolean {
  return restartBlocker(workspace) == null;
}

/** "Just now", "5 min ago", "3 hours ago", "3 days ago"; "Never" for a workspace never seen running. */
export function lastRunningText(lastReadyAt: number | null, now: number): string {
  if (lastReadyAt == null) return "Never";
  const minutes = Math.floor(Math.max(0, now - lastReadyAt) / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${plural(hours, "hour")} ago`;
  return `${plural(Math.floor(hours / 24), "day")} ago`;
}

/** The counts over the table, by state in the pill's order then any other by name, and how many have a job under way: "3 running · 4 stopped · 1 busy". */
export function countsText(workspaces: readonly Pick<AdminWorkspace, "state" | "busy">[]): string {
  if (workspaces.length === 0) return "No workspaces";
  const counts = new Map<string, number>();
  for (const workspace of workspaces) counts.set(workspace.state, (counts.get(workspace.state) ?? 0) + 1);
  const order = Object.keys(STATES);
  const states = [...counts.keys()].sort((a, b) => {
    const rank = (state: string): number => (order.includes(state) ? order.indexOf(state) : order.length);
    return rank(a) - rank(b) || a.localeCompare(b);
  });
  const parts = states.map((state) => `${counts.get(state)} ${(STATES[state]?.label ?? state).toLowerCase()}`);
  const busy = workspaces.filter((workspace) => workspace.busy != null).length;
  return [...parts, ...(busy > 0 ? [`${busy} busy`] : [])].join(" · ");
}

/** What the confirm dialog asks before Restart. */
export function restartQuestion(workspace: Pick<AdminWorkspace, "account">): { readonly title: string; readonly message: string; readonly action: string } {
  return {
    title: "Restart this workspace?",
    message: `${userText(workspace)}: it is backed up and started again with the app’s current settings. Whatever its bots are doing is cut off; files and chats are kept.`,
    action: "Restart",
  };
}

/** What the confirm dialog asks before Restart all, naming how many it will restart. */
export function restartAllQuestion(running: number): { readonly title: string; readonly message: string; readonly action: string } {
  return {
    title: `Restart ${plural(running, "running workspace")}?`,
    message: `${running === 1 ? "It is" : "Each is"} backed up and started again with the app’s current settings, and whatever its bots are doing is cut off. Stopped workspaces are left alone.`,
    action: "Restart all",
  };
}

/** What the page says once Restart all is under way. */
export function restartAllMessage(answer: RestartAllAnswer): string {
  const skipped = answer.skipped > 0 ? `; ${answer.skipped} skipped` : "";
  if (answer.restarted === 0) return `Nothing to restart${skipped}.`;
  return `Restarting ${plural(answer.restarted, "workspace")}${skipped}.`;
}
